import { useId, type ReactNode } from 'react';
import { cn } from '../utils/cn.js';

export type ComingSoonButtonVariant = 'primary' | 'secondary' | 'ghost' | 'outline';
export type ComingSoonButtonSize = 'sm' | 'md' | 'lg';

export interface ComingSoonButtonProps {
  /** Visible button label. */
  label: ReactNode;
  /**
   * The honest reason this action isn't wired yet. Rendered as `title` (tooltip)
   * and, if `showHelper` is true, also as visible helper text under the button.
   * Example: "Magic-link sign-in is being wired. Use your password for now."
   */
  reason: string;
  /** Optional icon slot rendered before the label. */
  icon?: ReactNode;
  /** Also render `reason` as a visible `.helper` line under the button. Default true. */
  showHelper?: boolean;
  /** Visual style. Defaults to `ghost` since coming-soon is by definition secondary. */
  variant?: ComingSoonButtonVariant;
  /** Button size. Defaults to `md`. */
  size?: ComingSoonButtonSize;
  className?: string;
}

const VARIANT_CLASS: Record<ComingSoonButtonVariant, string> = {
  primary:   'btn-primary',
  secondary: 'btn-secondary',
  ghost:     'btn-ghost',
  outline:   'btn-outline',
};

const SIZE_CLASS: Record<ComingSoonButtonSize, string> = {
  sm: 'btn-sm',
  md: '',
  lg: 'btn-lg',
};

/**
 * `<ComingSoonButton>` — the honest "this affordance is planned but not wired yet"
 * pattern that shows up 5+ times across the auth/checkout flow (Login magic-link,
 * RegisterStudent parental-consent, RegisterInstitution × 2 upload buttons, etc).
 *
 * Rules from `docs/design/system/copy-voice.md`:
 *   - Never a fake toast that lies about a file being downloaded or sent.
 *   - Never silently non-functional (a button that does nothing on click reads worse
 *     than one that says why).
 *   - `disabled` on the button + `title` tooltip + optional visible helper text.
 *
 * Renders as `<button type="button" disabled ...>` with the label + icon, plus an
 * `aria-describedby`-linked helper span (auto-wired via `useId`).
 *
 * Consumers should never call this for actions that are actually broken — it's for
 * *planned* features that aren't ready. If the action can never happen at all,
 * remove the button entirely.
 */
export function ComingSoonButton({
  label,
  reason,
  icon,
  showHelper = true,
  variant = 'ghost',
  size = 'md',
  className,
}: ComingSoonButtonProps) {
  const helperId = useId();
  return (
    <>
      <button
        type="button"
        disabled
        title={reason}
        aria-describedby={showHelper ? helperId : undefined}
        className={cn('btn', VARIANT_CLASS[variant], SIZE_CLASS[size], className)}
      >
        {icon}
        {label}
      </button>
      {showHelper && (
        <span id={helperId} className="helper">
          {reason}
        </span>
      )}
    </>
  );
}
